import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  BillableIcon,
  Currency,
  DateTime,
  DeleteConfirmation,
  Icon,
  IconButton,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  MarkupIcon,
  Page,
  Tooltip,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useRowSelection } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import TitleTooltip from '../../TitleTooltip';
import AddMultipleDialog from './AddMultipleDialog';
import BulkDelete from './BulkDelete';
import ProjectExpenseForm from './ProjectExpenseForm';
import SettingsActionsBanner from '../../SettingsActionsBanner';

const InvoicedIcon = styled(Icon)`
  color: ${colors.primary};
`;

export default function ProjectExpenses({ project }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).projectExpenses().get();
    // A billable expense has no place on a non-billable project (typically left over from a billing-type
    // change), so the ledger and its total leave those rows out.
    setData(data.filter((item) => !item.isBillable || project.isBillable));
  }, [api, workspace.id, project]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selection = useRowSelection({ rows: data });

  const [dialog, setDialog] = useState(null);
  const toast = useToast();

  const handleSaved = async () => {
    await fetchData();
    setDialog(null);
  };

  const handleShowForm = (projectExpense = null) => {
    setDialog(
      <ProjectExpenseForm
        projectId={project.id}
        projectExpenseId={projectExpense?.id}
        onSaved={handleSaved}
        onClose={() => setDialog(null)}
      />,
    );
  };

  const handleShowMultipleForm = () => {
    setDialog(
      <AddMultipleDialog
        currency={project.currency}
        project={project}
        onSaved={handleSaved}
        onCancel={() => setDialog(null)}
      />,
    );
  };

  const confirmation = useConfirmation();
  const handleDelete = async (expense) => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        title="Delete Project Expense"
        resolve={async (result) => {
          if (result) {
            await api.www.workspaces(workspace.id).projectSettings(project.id).projectExpenses(expense.id).delete();
            toast.success('Project Expense deleted successfully.');
            await fetchData();
            selection.deselect(expense.id);
          }
          resolve(result);
        }}>
        Are you sure you want to delete this project expense?
      </DeleteConfirmation>
    ));
  };

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        projectId={project.id}
        ids={selection.ids}
        currency={project.currency}
        onComplete={async () => {
          selection.clear();
          await fetchData();
        }}
        onClose={resolve}
      />
    ));
  };

  if (!data) return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Project Expenses</Page.Title>
        <TitleTooltip message="Track project expenses that are not reflected on a workspace member's expense report." />

        {project.permissions.edit && (
          <Page.Actions>
            <IconButton icon="clone-plus" tooltip="Add Multiple" onClick={handleShowMultipleForm} />

            <IconButton icon="plus" tooltip="Add" onClick={() => handleShowForm(null)} />
          </Page.Actions>
        )}
      </Page.TabHeader>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListViewSelection.Column
              testId="project_expenses_bulk_checkbox"
              selection={selection}
              isVisible={project.permissions.edit}
              disabled={!data.length}
            />
            <ListView.Column width="8rem">Date</ListView.Column>
            <ListView.Column minWidth="10rem">Expense Category</ListView.Column>
            <ListView.Column minWidth="8rem">Vendor</ListView.Column>
            <ListView.Column minWidth="20rem">Notes</ListView.Column>
            <ListView.Column width="7rem" align="center">
              Billable
            </ListView.Column>
            <ListView.Column width="10rem" align="right">
              Amount
            </ListView.Column>
            <ListView.Column width="3rem" />
            <ListViewActions.Column isVisible={project.permissions.edit} />
          </ListView.Header>

          <ListView.Body>
            {data.map((expense) => {
              const isInvoiced = expense.invoice && expense.invoice.statusId !== 'draft';
              const areActionsDisabled = isInvoiced;
              const tooltip = isInvoiced ? "Invoiced project expenses can't be modified." : undefined;

              return (
                <ListView.Row
                  key={expense.id}
                  onClick={!areActionsDisabled && project.permissions.edit ? () => handleShowForm(expense) : undefined}>
                  <ListViewSelection.Cell id={expense.id} selection={selection} testId="checkbox_project_expense" />
                  <ListView.Cell>
                    <DateTime value={expense.date} />
                  </ListView.Cell>
                  <ListView.Cell>{expense.expenseCategory.name}</ListView.Cell>
                  <ListView.Cell>{expense.vendor}</ListView.Cell>
                  <ListView.Cell>{expense.notes}</ListView.Cell>
                  <ListView.Cell>
                    {expense.markup > 0 && expense.isBillable ? (
                      <MarkupIcon total={expense.total} currency={expense.project.currency} />
                    ) : (
                      <BillableIcon value={expense.isBillable} />
                    )}
                  </ListView.Cell>
                  <ListView.Cell>
                    <Currency value={expense.amount} currency={expense.project.currency} />
                  </ListView.Cell>
                  <ListView.Cell>
                    {isInvoiced && (
                      <Tooltip message="An invoice has been published for this project expense.">
                        <InvoicedIcon icon="file-invoice-dollar" />
                      </Tooltip>
                    )}
                  </ListView.Cell>
                  <ListViewActions isVisible={project.permissions.edit}>
                    <ListViewActions.Edit
                      disabled={areActionsDisabled}
                      tooltip={tooltip}
                      onClick={() => handleShowForm(expense)}
                    />
                    <hr />
                    <ListViewMenu>
                      <ListViewMenu.Item disabled={areActionsDisabled} onClick={() => handleShowForm(expense)}>
                        Edit
                      </ListViewMenu.Item>
                      <ListViewMenu.DeleteItem
                        disabled={areActionsDisabled}
                        tooltip={tooltip}
                        onClick={() => handleDelete(expense)}>
                        Delete
                      </ListViewMenu.DeleteItem>
                    </ListViewMenu>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length > 0 && (
              <ListView.TotalRow>
                <ListView.Cell />
                <ListView.Cell>Total</ListView.Cell>
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell />
                <ListView.Cell>
                  <Currency value={_.sumBy(data, 'amount')} currency={project.currency} />
                </ListView.Cell>
                <ListView.Cell />
                <ListViewActions.Cell isVisible={project.permissions.edit} />
              </ListView.TotalRow>
            )}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={data.length} label="Project Expense" />
        </ListView>
      </Page.ListView>

      {dialog}

      {selection.any && (
        <SettingsActionsBanner>
          <BulkActionMenu count={selection.count}>
            {({ setIsOpen }) => (
              <BulkActionMenu.Item
                onClick={() => {
                  setIsOpen(false);
                  handleBulkDelete();
                }}>
                Delete
              </BulkActionMenu.Item>
            )}
          </BulkActionMenu>
        </SettingsActionsBanner>
      )}
    </>
  );
}
